<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { ui } from '../editor/bridge';
import { keepCanvasSelection } from '../editor/dismiss';
import { chatThreads, deleteChat, deleteInbox, inboxThreads, openCanvasChat, openInboxChat, postChat, postInbox, type ChatChannel, type ChatLine, type InboxLine } from '../chat';
import { locale, t } from '../i18n';
import { useApp } from '../stores/app';
import { platformFeedTools, type PlatformFeedTool } from '../feed-tools';
import { isFeedToolOpen, moduleFeedTools, runFeedTool, type ModuleFeedTool } from '../modules';
import Icon, { ICON_NAMES } from './Icon.vue';

/** Side chat: AI, team and personal notes of the open canvas, switched at the top. */
type FeedSection = 'chat' | 'notices' | 'templates' | 'tools';

const app = useApp();
const sections: { id: FeedSection; icon: string }[] = [
  { id: 'tools', icon: 'wrench' },
  { id: 'templates', icon: 'apps' },
  { id: 'chat', icon: 'chat' },
  { id: 'notices', icon: 'bell' },
];
const section = ref<FeedSection>(sections[0].id);
const channel = ref<ChatChannel>('ai');
const draft = ref('');
const reply = ref<ChatLine | null>(null);
const linked = ref<{ id: string; name: string } | null>(null);
const menu = ref<{ x: number; y: number; line: ChatLine } | null>(null);
const picked = ref<string | null>(null);
let pickedTimer = 0;
const root = ref<HTMLElement | null>(null);
const thread = ref<HTMLElement | null>(null);
const box = ref<HTMLTextAreaElement | null>(null);
/** Just under the round button, or under the properties bar when that bar reaches this panel. */
const topPx = ref('64px');

/** Home and the other screens have no open canvas, so there is no team thread. */
const inbox = computed(() => app.view !== 'editor' || !app.active);
const channels = computed<ChatChannel[]>(() => (inbox.value ? ['ai', 'personal'] : ['ai', 'team', 'personal']));
const lines = computed(() => (inbox.value ? inboxThreads[channel.value === 'team' ? 'ai' : channel.value] : chatThreads[channel.value]));
const empty = computed(() => {
  if (inbox.value) return t(channel.value === 'personal' ? 'feed.emptyPersonal' : 'feed.emptyAi');
  return t(channel.value === 'ai' ? 'chat.emptyAi' : channel.value === 'team' ? 'chat.emptyTeam' : 'chat.emptyPersonal');
});
const bang = computed(() => {
  if (!inbox.value || section.value !== 'chat') return null;
  const match = /(^|\s)!([^\n]*)$/.exec(draft.value);
  return match ? match[2] : null;
});
const canvasHits = computed(() => {
  if (bang.value === null) return [];
  const query = bang.value.trim().toLowerCase();
  return app.canvases.filter((canvas) => !query || canvas.name.toLowerCase().includes(query)).slice(0, 8);
});
/** The canvas this conversation is about: the open one, or the one picked with !. */
const skill = computed(() => {
  if (!inbox.value && app.active) {
    const name = app.canvases.find((canvas) => canvas.id === app.active)?.name;
    return name ? { name, removable: false } : null;
  }
  return linked.value ? { name: linked.value.name, removable: true } : null;
});

const toolCtx = computed(() => ({ app, canvas: !inbox.value, readOnly: ui.readOnly }));
const coreTools = computed(() => platformFeedTools(toolCtx.value));
/** Module tools work on the open canvas; on other screens the section only says they are there. */
const modTools = computed(() => moduleFeedTools(ICON_NAMES));
const svgUrl = (svg: string) => 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);

const moduleKey = (tool: ModuleFeedTool) => tool.moduleId + ':' + tool.command;
/** Which tools have something open, by `data-feed-tool` key. */
const openTools = computed(() => {
  const out = new Set<string>();
  for (const tool of coreTools.value) if (tool.active?.()) out.add('platform:' + tool.id);
  for (const tool of modTools.value) if (isFeedToolOpen(tool)) out.add(moduleKey(tool));
  return out;
});
/**
 * When a tool's card closed itself on this very press (cards close on a press outside them), the
 * click that follows must not open it again: the press on the tool's name meant "close".
 */
const closedAt = new Map<string, number>();
let pressAt = Infinity;
watch(openTools, (now, before) => {
  for (const key of before ?? []) if (!now.has(key)) closedAt.set(key, performance.now());
});
function justClosed(key: string): boolean {
  const hit = (closedAt.get(key) ?? -Infinity) >= pressAt;
  pressAt = Infinity;
  return hit;
}

function runCore(tool: PlatformFeedTool): void {
  const closed = justClosed('platform:' + tool.id);
  if (!tool.active?.() && closed) return;
  tool.run(toolCtx.value);
}

function runModule(tool: ModuleFeedTool): void {
  const closed = justClosed(moduleKey(tool));
  if (!isFeedToolOpen(tool) && closed) return;
  void runFeedTool(tool);
}

const WINDOW = '[role="dialog"]';
/**
 * A press outside the feed closes it; its own launch buttons keep their toggle. While another window
 * is open (one a tool opened, say), a press on the free space closes only that window; a press inside
 * another window means the user moved on to it, and the feed closes.
 */
function onOutside(e: PointerEvent): void {
  const target = e.target as Element | null;
  if (!target || root.value?.contains(target)) return;
  if (target.closest?.('[data-act="feed"], [data-act="feed-home"], [data-act="feed-profile"], .chat-msg-menu, .chat-menu-backdrop')) return;
  const inWindow = !!target.closest?.(WINDOW);
  const windowOpen = [...document.querySelectorAll(WINDOW)].some((el) => !root.value?.contains(el));
  if (!inWindow && windowOpen) return;
  ui.chatOpen = false;
  keepCanvasSelection(e);
}

function formatTime(at: number): string {
  return new Intl.DateTimeFormat(locale.value, { hour: '2-digit', minute: '2-digit' }).format(at);
}

function grow(): void {
  const el = box.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 120) + 'px';
}

async function scrollDown(): Promise<void> {
  await nextTick();
  const el = thread.value;
  if (el) el.scrollTop = el.scrollHeight;
}

function authorName(line: ChatLine): string {
  return line.author.name || t('chat.you');
}

function canvasOf(line: ChatLine): string | null {
  const id = (line as InboxLine).canvasId;
  return typeof id === 'string' && id ? id : null;
}

function canvasName(id: string): string {
  return app.canvases.find((canvas) => canvas.id === id)?.name ?? t('feed.canvasGone');
}

/** Skill chip on a sent line: a picked canvas, or the canvas this thread belongs to. */
function skillOf(line: ChatLine): string | null {
  const id = canvasOf(line);
  if (id) return canvasName(id);
  if (!inbox.value) return skill.value?.name ?? null;
  return null;
}

function pickCanvas(canvas: { id: string; name: string }): void {
  linked.value = { id: canvas.id, name: canvas.name };
  draft.value = draft.value.replace(/(^|\s)!([^\n]*)$/, '$1').trim();
}

function quoted(line: ChatLine): string | null {
  if (!line.replyTo) return null;
  return lines.value.find((item) => item.id === line.replyTo)?.text ?? t('chat.replyGone');
}

function targetOf(line: ChatLine): ChatLine | null {
  if (!line.replyTo) return null;
  return lines.value.find((item) => item.id === line.replyTo) ?? null;
}

function openMenu(e: MouseEvent, line: ChatLine): void {
  const width = 168;
  const height = 84;
  menu.value = {
    x: Math.max(8, Math.min(e.clientX, window.innerWidth - width - 8)),
    y: Math.max(8, Math.min(e.clientY, window.innerHeight - height - 8)),
    line,
  };
}

function startReply(line: ChatLine): void {
  reply.value = line;
  menu.value = null;
  box.value?.focus();
}

function clearPick(): void {
  window.clearTimeout(pickedTimer);
  pickedTimer = 0;
  picked.value = null;
}

function jumpTo(line: ChatLine): void {
  const target = targetOf(line);
  if (!target) return;
  window.clearTimeout(pickedTimer);
  picked.value = null;
  void nextTick(() => {
    picked.value = target.id;
    thread.value?.querySelector(`[data-line="${CSS.escape(target.id)}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    pickedTimer = window.setTimeout(() => {
      if (picked.value === target.id) picked.value = null;
    }, 5600);
  });
}

function onEsc(): void {
  if (menu.value) {
    menu.value = null;
    return;
  }
  ui.chatOpen = false;
}

function send(): void {
  if (bang.value !== null && canvasHits.value[0]) {
    pickCanvas(canvasHits.value[0]);
    return;
  }
  const text = draft.value;
  if (!text.trim()) return;
  draft.value = '';
  const replyTo = reply.value?.id ?? null;
  if (inbox.value) postInbox(channel.value === 'personal' ? 'personal' : 'ai', text, replyTo, linked.value?.id ?? null);
  else postChat(channel.value, text, replyTo);
  reply.value = null;
  if (box.value) box.value.style.height = '';
  void scrollDown();
}

function remove(line: ChatLine): void {
  if (reply.value?.id === line.id) reply.value = null;
  if (picked.value === line.id) clearPick();
  menu.value = null;
  if (inbox.value) deleteInbox(channel.value === 'personal' ? 'personal' : 'ai', line.id);
  else deleteChat(channel.value, line.id);
}

function place(): void {
  const launch = (app.view === 'editor' ? document.querySelector('.stage [data-act="feed"]') : document.querySelector('[data-act="feed-home"], [data-act="feed-profile"]'))?.getBoundingClientRect();
  let top = launch ? launch.bottom + 10 : 64;
  if (app.view === 'editor') {
    const bar = document.querySelector('.stage .propbar-wrap');
    const panel = root.value?.getBoundingClientRect();
    if (bar instanceof HTMLElement && panel) {
      const b = bar.getBoundingClientRect();
      const overlaps = b.width > 8 && b.bottom > top - 4 && b.right > panel.left + 8 && b.left < panel.right - 8;
      if (overlaps) top = b.bottom + 10;
    }
  }
  topPx.value = `${Math.round(top)}px`;
}

watch(
  () => [app.view, app.active] as const,
  ([view, id]) => {
    if (view === 'editor' && id) void openCanvasChat(id);
    else {
      if (channel.value === 'team') channel.value = 'ai';
      void openInboxChat();
    }
    void nextTick(() => requestAnimationFrame(place));
    void scrollDown();
  },
  { immediate: true },
);
watch(channel, () => {
  reply.value = null;
  menu.value = null;
  clearPick();
  void scrollDown();
});
watch(
  () => ui.chatOpen,
  (open) => {
    if (open) section.value = sections[0].id;
  },
);
watch(section, () => {
  menu.value = null;
});
watch(() => lines.value.length, () => void scrollDown());
watch(() => ui.selection.length + (ui.barBelow ? 1 : 0), () => void nextTick(() => requestAnimationFrame(place)));

onMounted(() => {
  void scrollDown();
  box.value?.focus();
  void nextTick(() => requestAnimationFrame(place));
  window.addEventListener('resize', place);
  window.addEventListener('pointerdown', onOutside, true);
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', place);
  window.removeEventListener('pointerdown', onOutside, true);
  window.clearTimeout(pickedTimer);
});
</script>

<template>
  <aside ref="root" class="float card chat-panel" :class="{ 'on-home': inbox }" data-chat-panel data-vp-dismiss :style="{ top: topPx, '--feed-top': topPx }" :aria-label="t('feed.open')" @pointerdown.stop @keydown.stop @keydown.esc="onEsc">
    <header>
      <div class="seg-pills channels sections" role="tablist" :aria-label="t('feed.sections')">
        <button
          v-for="s in sections"
          :key="s.id"
          type="button"
          role="tab"
          :class="{ active: section === s.id }"
          :aria-selected="section === s.id"
          :aria-label="t('feed.' + s.id)"
          :title="s.id === 'chat' ? t('feed.' + s.id) + ' · ' + t('app.beta') : t('feed.' + s.id)"
          :data-feed-section="s.id"
          @click="section = s.id"
        >
          <Icon :name="s.icon" :size="14" /><span>{{ t('feed.' + s.id) }}</span><em v-if="s.id === 'chat'" class="beta" data-feed-beta>{{ t('app.beta') }}</em>
        </button>
      </div>
      <button type="button" class="icon-btn" :aria-label="t('common.close')" :title="t('feed.close')" @click="ui.chatOpen = false"><Icon name="close" :size="16" /></button>
    </header>
    <div v-if="section === 'chat'" class="seg-pills channels sub" role="tablist" :aria-label="t('chat.channels')">
      <button
        v-for="id in channels"
        :key="id"
        type="button"
        role="tab"
        :class="{ active: channel === id }"
        :aria-selected="channel === id"
        :data-chat-channel="id"
        @click="channel = id"
      >
        {{ t('chat.' + id) }}
      </button>
    </div>
    <div v-if="section === 'tools'" class="thread tools" data-feed-tools>
      <p class="group">{{ t('feed.toolsPlatform') }}</p>
      <button v-for="tool in coreTools" :key="tool.id" type="button" class="tool" :class="{ active: openTools.has('platform:' + tool.id) }" :aria-pressed="tool.active ? openTools.has('platform:' + tool.id) : undefined" :data-feed-tool="'platform:' + tool.id" @pointerdown="pressAt = $event.timeStamp" @click="runCore(tool)">
        <span class="tool-icon"><Icon :name="tool.icon" :size="16" /></span>
        <span class="tool-text">
          <span class="tool-name">{{ tool.label() }}</span>
        </span>
      </button>
      <template v-if="modTools.length">
        <p class="group">{{ t('feed.toolsModules') }}</p>
        <p v-if="inbox && modTools.some((x) => !x.home)" class="hint">{{ t('feed.toolsOpenCanvas') }}</p>
        <button
          v-for="tool in modTools"
          :key="tool.moduleId + ':' + tool.command"
          type="button"
          class="tool"
          :class="{ active: openTools.has(moduleKey(tool)) }"
          :aria-pressed="openTools.has(moduleKey(tool))"
          :disabled="inbox && !tool.home"
          :data-feed-tool="moduleKey(tool)"
          @pointerdown="pressAt = $event.timeStamp"
          @click="runModule(tool)"
        >
          <span class="tool-icon">
            <img v-if="tool.svg" class="mod-icon" :src="svgUrl(tool.svg)" alt="" />
            <Icon v-else :name="tool.icon" :size="16" />
          </span>
          <span class="tool-text">
            <span class="tool-name">{{ tool.label }}</span>
          </span>
        </button>
      </template>
    </div>
    <div v-else-if="section !== 'chat'" class="thread">
      <p class="empty">{{ t(section === 'notices' ? 'feed.noticesEmpty' : 'feed.templatesEmpty') }}</p>
      <p class="hint">{{ t('feed.syncHint') }}</p>
    </div>
    <div v-else ref="thread" class="thread">
      <p v-if="!lines.length" class="empty">{{ empty }}</p>
      <div v-for="m in lines" :key="m.id" class="line" :class="{ picked: picked === m.id }" :data-line="m.id" @contextmenu.prevent.stop="openMenu($event, m)">
        <div class="msg">
          <span class="who">{{ authorName(m) }}</span>
          <button v-if="targetOf(m)" type="button" class="quote" @click="jumpTo(m)">{{ targetOf(m)?.text }}</button>
          <p v-else-if="quoted(m)" class="quote gone">{{ quoted(m) }}</p>
          <div class="bubble">
            <span v-if="skillOf(m)" class="skill"><Icon name="grid" :size="12" /><span>{{ skillOf(m) }}</span></span>
            <p>{{ m.text }}</p>
            <time :datetime="new Date(m.at).toISOString()">{{ formatTime(m.at) }}</time>
          </div>
        </div>
        <span class="avatar" aria-hidden="true">
          <img v-if="m.author.avatar" :src="m.author.avatar" alt="" />
          <Icon v-else name="user" :size="14" />
        </span>
      </div>
    </div>
    <form v-if="section === 'chat'" class="composer" @submit.prevent="send">
      <ul v-if="canvasHits.length" class="hits" role="listbox" :aria-label="t('feed.pickCanvas')">
        <li v-for="c in canvasHits" :key="c.id">
          <button type="button" role="option" @mousedown.prevent="pickCanvas(c)">{{ c.name }}</button>
        </li>
      </ul>
      <p v-if="reply" class="replying">
        <span>{{ t('chat.replying') }}: {{ reply.text }}</span>
        <button type="button" @click="reply = null">{{ t('common.cancel') }}</button>
      </p>
      <div class="field">
        <span v-if="skill" class="skill">
          <Icon name="grid" :size="12" />
          <span>{{ skill.name }}</span>
          <button v-if="skill.removable" type="button" class="skill-x" :aria-label="t('common.cancel')" @click="linked = null"><Icon name="close" :size="11" /></button>
        </span>
        <textarea ref="box" v-model="draft" rows="1" :placeholder="t('chat.placeholder')" :aria-label="t('chat.placeholder')" @input="grow" @keydown.enter.exact.prevent="send"></textarea>
      </div>
      <button type="submit" class="send" :disabled="!draft.trim()" :aria-label="t('chat.send')" :title="t('chat.send')"><Icon name="send" :size="16" /></button>
    </form>
    <Teleport to="body">
      <div v-if="menu" class="chat-menu-backdrop" @pointerdown="menu = null" @contextmenu.prevent="menu = null"></div>
      <div v-if="menu" class="chat-msg-menu" role="menu" :style="{ left: menu.x + 'px', top: menu.y + 'px' }" @pointerdown.stop>
        <button type="button" role="menuitem" @click="startReply(menu.line)">{{ t('chat.reply') }}</button>
        <button type="button" role="menuitem" class="danger" @click="remove(menu.line)">{{ t('chat.deleteMsg') }}</button>
      </div>
    </Teleport>
  </aside>
</template>

<style scoped>
.chat-panel {
  position: fixed;
  z-index: 22;
  top: 64px;
  right: 20px;
  /* as tall as its content, up to a fixed card: past that the thread scrolls */
  height: fit-content;
  max-height: min(651px, calc(100dvh - var(--feed-top, 64px) - var(--bottom-reserve, 64px) - 12px));
  width: 340px;
  max-width: calc(100% - 40px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--panel);
  user-select: text;
}
header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 8px 10px 10px;
  border-bottom: 1px solid var(--border);
  user-select: none;
}
.channels {
  flex: 1;
  min-width: 0;
  height: 34px;
}
.channels button {
  flex: 1;
  min-width: 0;
  padding: 0 6px;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sections {
  display: flex;
}
/* the chosen section grows to show its name; the rest stay icon-only */
.sections button {
  flex: 0 0 36px;
  width: 36px;
  max-width: 36px;
  padding: 0;
  gap: 5px;
  transition:
    flex-grow 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    flex-basis 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    max-width 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    width 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    padding 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    background 0.2s ease;
}
.sections button.active {
  flex: 1 0 36px;
  width: auto;
  max-width: 240px;
  padding: 0 10px;
}
.sections button span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 0;
  opacity: 0;
  transition:
    max-width 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.18s ease;
}
.sections button.active span {
  max-width: 12em;
  opacity: 1;
}
.sections button[data-feed-section='chat'] {
  flex: 0 0 auto;
  width: auto;
  max-width: none;
  padding: 0 8px 0 10px;
}
.sections button[data-feed-section='chat'].active {
  flex: 1 0 auto;
}
.beta {
  padding: 1px 7px;
  border-radius: var(--radius-control);
  background: color-mix(in srgb, var(--accent) 16%, transparent);
  color: var(--accent-text);
  font-size: 10.5px;
  font-style: normal;
  font-weight: 600;
  letter-spacing: 0.03em;
}
@media (prefers-reduced-motion: reduce) {
  .sections button,
  .sections button span {
    transition: none;
  }
}
.sub {
  display: flex;
  flex: none;
  align-self: stretch;
  height: 34px;
  margin: 8px 10px 0;
}
.tools {
  gap: 2px;
  padding: 8px;
}
.group {
  margin: 6px 8px 4px;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-3);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.group:not(:first-child) {
  margin-top: 14px;
}
.tools .hint {
  margin: 0 8px 6px;
  max-width: none;
  text-align: left;
}
.tool {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 6px 8px;
  border: none;
  border-radius: 12px;
  background: none;
  color: var(--text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.tool:hover:not(:disabled) {
  background: var(--hover);
}
.tool.active {
  background: var(--accent-soft);
}
.tool.active .tool-icon {
  background: color-mix(in srgb, var(--accent) 18%, transparent);
  color: var(--accent-text);
}
.tool:disabled {
  opacity: 0.5;
  cursor: default;
}
.tool-icon {
  width: 32px;
  height: 32px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: var(--hover);
  color: var(--text-2);
}
.tool-icon .mod-icon {
  width: 16px;
  height: 16px;
}
.tool-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.tool-name {
  font-size: 13.5px;
  font-weight: 500;
}
.hint {
  margin: 0 auto;
  max-width: 240px;
  text-align: center;
  color: var(--text-3);
  font-size: 12px;
}
.skill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  height: 22px;
  padding: 0 8px 0 6px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent-text);
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}
.skill > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bubble .skill {
  margin-bottom: 6px;
}
.skill-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  margin-right: -2px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
.skill-x:hover {
  background: color-mix(in srgb, var(--accent) 18%, transparent);
}
.field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
  border: 1px solid var(--border-strong);
  border-radius: 16px;
  padding: 8px 12px;
  background: var(--input);
}
.field:focus-within {
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}
.hits {
  grid-column: 1 / -1;
  margin: 0;
  padding: 4px;
  list-style: none;
  max-height: 160px;
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--panel);
}
.hits button {
  width: 100%;
  border: none;
  background: none;
  text-align: left;
  padding: 6px 8px;
  border-radius: 8px;
  font: inherit;
  font-size: 13px;
  color: var(--text);
  cursor: pointer;
}
.hits button:hover {
  background: var(--hover);
}
.on-home {
  max-height: min(651px, calc(100dvh - var(--feed-top, 64px) - 16px));
}
.thread {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.empty {
  margin: auto;
  max-width: 240px;
  text-align: center;
  color: var(--text-3);
  font-size: 13px;
  line-height: 1.45;
}
.line {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 8px;
}
.msg {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  max-width: calc(100% - 36px);
  min-width: 0;
}
.who {
  margin: 0 4px 3px;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-2);
}
.avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--hover);
  color: var(--text-3);
  flex: none;
  overflow: hidden;
}
.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.quote,
.replying span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.quote {
  max-width: 100%;
  margin: 0 0 4px;
  padding: 4px 8px;
  border: none;
  border-left: 2px solid var(--accent);
  border-radius: 8px;
  background: var(--hover);
  color: var(--text-2);
  font: inherit;
  font-size: 12px;
  text-align: left;
}
button.quote {
  cursor: pointer;
}
button.quote:hover {
  color: var(--text);
}
.quote.gone {
  cursor: default;
}
.line.picked .bubble {
  animation: chat-pick 5.6s linear forwards;
}
.bubble {
  max-width: 100%;
  padding: 8px 12px 5px;
  border-radius: 16px 16px 6px 16px;
  background: var(--accent-soft);
}
@keyframes chat-pick {
  0%, 89.3% {
    box-shadow: 0 0 0 2px var(--accent);
    background: color-mix(in srgb, var(--accent) 24%, var(--accent-soft));
  }
  100% {
    box-shadow: 0 0 0 0 transparent;
    background: var(--accent-soft);
  }
}
.replying button {
  border: none;
  background: none;
  padding: 0;
  color: var(--text-3);
  font: inherit;
  font-size: 11px;
  cursor: pointer;
}
.replying button:hover {
  color: var(--text);
}
.chat-menu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 80;
}
.chat-msg-menu {
  position: fixed;
  z-index: 81;
  min-width: 148px;
  padding: 4px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--panel);
  box-shadow: var(--shadow);
  display: flex;
  flex-direction: column;
}
.chat-msg-menu button {
  border: none;
  background: none;
  text-align: left;
  padding: 8px 10px;
  border-radius: 8px;
  font: inherit;
  font-size: 13px;
  color: var(--text);
  cursor: pointer;
}
.chat-msg-menu button:hover {
  background: var(--hover);
}
.chat-msg-menu button.danger {
  color: var(--danger);
}
.replying {
  display: flex;
  align-items: center;
  gap: 8px;
  grid-column: 1 / -1;
  margin: 0;
  min-width: 0;
  color: var(--text-2);
  font-size: 12px;
}
.replying span {
  flex: 1;
  min-width: 0;
}
.bubble p {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.45;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
time {
  display: block;
  margin-top: 2px;
  font-size: 10.5px;
  color: var(--text-3);
  text-align: right;
}
.composer {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 12px 12px;
  border-top: 1px solid var(--border);
}
textarea {
  width: 100%;
  min-width: 0;
  min-height: 22px;
  max-height: 120px;
  resize: none;
  border: none;
  padding: 0;
  background: transparent;
  color: var(--text);
  font: inherit;
  font-size: 13.5px;
  line-height: 1.4;
}
textarea:focus {
  outline: none;
}
.send {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: var(--accent);
  color: var(--on-accent);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
}
.send:disabled {
  opacity: 0.35;
  cursor: default;
}
.send:hover:not(:disabled) {
  background: var(--accent-2);
}
@media (max-width: 760px) {
  .chat-panel {
    left: 16px;
    right: 16px;
    width: auto;
  }
}
</style>
